import Link from "next/link";
import React from "react";
import { FaCheckCircle } from "react-icons/fa";



// Training location shown as a compact inline chip.
// Replaces the former full-width LocationCard grid, which took three rows on mobile
// to communicate six single words.
const LocationChip = ({ location }) => (
    <li className="flex items-center gap-1.5 bg-white/10 backdrop-blur-md rounded-full pl-2 pr-3 py-1.5 border border-white/20">
        <FaCheckCircle className="text-orange-400 text-xs shrink-0" />
        <span className="text-xs font-medium text-white/80 whitespace-nowrap">{location}</span>
    </li>
)

// Fitness Goal Card Component - Gen-Z glassmorphism
const FitnessGoalCard = ({ item }) => (
    <li>
        <Link href={item.link} prefetch={false}>
            {/* 2-up on mobile: the description is hidden below md so six goals occupy
                three short rows instead of six tall ones. The goal name (the actual
                internal-link anchor text) is always rendered. */}
            <div className="group relative h-full bg-white/10 backdrop-blur-md rounded-2xl p-2.5 md:p-4 border border-white/20 hover:bg-white/20 hover:border-white/40 hover:scale-[1.02] transition-all duration-300 cursor-pointer">
                <div className="flex items-center gap-2 md:gap-3">
                    <span className="text-lg md:text-2xl shrink-0">{item.icon}</span>
                    <div>
                        <span className="font-semibold text-xs md:text-sm text-white/90 group-hover:text-white transition-colors block">
                            {item.goal}
                        </span>
                        <span className="hidden md:block text-xs text-white/50">{item.desc}</span>
                    </div>
                </div>
            </div>
        </Link>
    </li>
);

// NOTE: a StatCard component previously lived here but was dead code - its only usage
// was commented out, and it called a `useAnimatedCounter` hook that is neither defined
// nor imported in this file, so re-enabling it would have crashed the page. Removed.
// If the stats block is revived, import useAnimatedCounter from PersonalTrainer.js first.

export default function TrainerValueProposition() {
    const trainingLocations = ['Your Home', 'Condo Gym', 'Public Gym', 'Outdoor Parks', 'GymPods', 'Office Gym']

    const fitnessGoals = [
        { 
            goal: 'Weight Loss', 
            icon: '🔥', 
            link: '/sg/personal-trainer/weight-loss-trainer', 
            desc: 'Burn fat & achieve sustainable weight loss' 
        },
        { 
            goal: 'Muscle Building & Toning', 
            icon: '💪', 
            link: '/sg/personal-trainer/body-building-trainer', 
            desc: 'Build muscle, strength, and definition' 
        },
        { 
            goal: 'Prenatal & Postpartum', 
            icon: '🤰', 
            link: '/sg/personal-trainer/prenatal-postpartum-training', 
            desc: 'Safe training during & after pregnancy' 
        },
        { 
            goal: 'Elderly Fitness', 
            icon: '🧓', 
            link: '/sg/personal-trainer/personal-trainer-for-elderly', 
            desc: 'Improve strength, balance & mobility' 
        },
        { 
            goal: 'Functional Fitness', 
            icon: '🏃', 
            link: '/sg/personal-trainer/personal-trainer-for-functional-fitness', 
            desc: 'Train for everyday strength & movement' 
        },
        { 
            goal: 'IPPT Training', 
            icon: '🎖️', 
            link: '/sg/blogs/health-fitness/ippt-personal-trainer-singapore', 
            desc: 'Prepare & improve your IPPT score' 
        }
    ];
    return (
        <section className="relative bg-slate-900 py-5 md:py-16 overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 space-y-10 md:space-y-16">                               
            {/* Goals + locations merged into one section.
                Previously these were two full-width blocks costing ~900-1100px of mobile
                scroll for under 60 words of unique copy, with "home / condo gym / park"
                already stated in the H1, hero copy and objections cards. The six goal
                cards are kept in full - they are the hub's internal links to the child
                pages - while locations are demoted to an inline chip row. */}
            <div className="relative rounded-4xl p-4 md:p-10 overflow-hidden bg-slate-900">
                {/* Mesh gradient blobs */}
                <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-orange-500/15 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2" />

                <div className="relative">
                    <div className="flex justify-center items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 mx-auto w-fit">
                        <span className="text-sm">🎯</span>
                        <span className="text-xs font-medium text-white/70">Goal-Based Training</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2 text-center tracking-tight">
                        Personal Training for Every Goal &mdash; At Home, Your Condo Gym or Outdoors
                    </h2>
                    <p className="text-white/50 mb-5 text-center text-sm max-w-md mx-auto">
                        Whatever you are training for, we match you with a certified trainer who specialises in it.
                    </p>

                    {/* Training locations as inline chips */}
                    <ul className="flex flex-wrap justify-center gap-2 mb-6">
                        {trainingLocations.map((location, i) => (
                            <LocationChip key={i} location={location} />
                        ))}
                    </ul>

                    <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                        {fitnessGoals.map((item) => (
                            <FitnessGoalCard key={item.goal} item={item} />
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    </section>
    )
}